/**
 * Utility functions for working with star map images.
 */

/**
 * Finds star map image URL from slot selections.
 * 
 * @param {Array} slots - Array of slot selections to search through
 * @returns {string | null} The star map image URL (data URL or regular URL) or null if not found
 */
export function findStarMapImageUrl(slots) {
  if (!slots || !Array.isArray(slots)) {
    return null;
  }

  // Search all slots for one that has star map data with an image URL.
  const starMapSlot = slots.find((s) => {
    if (!s.selected) return false;
    
    let selectedData = s.selected;
    
    // Parse if it's a JSON string.
    if (typeof selectedData === 'string') {
      try {
        selectedData = JSON.parse(selectedData);
      } catch {
        return false;
      }
    }
    
    // Check if it's an object with starMapImageUrl.
    if (selectedData && typeof selectedData === 'object' && selectedData !== null) {
      // Use bracket notation to safely access starMapImageUrl.
      return 'starMapImageUrl' in selectedData && selectedData['starMapImageUrl'];
    }
    
    return false;
  });
  
  if (!starMapSlot || !starMapSlot.selected) {
    return null;
  }
  
  // Extract the starMapImageUrl.
  let selectedData = starMapSlot.selected;
  if (typeof selectedData === 'string') {
    try {
      selectedData = JSON.parse(selectedData);
    } catch {
      return null;
    }
  }
  
  // Use bracket notation to safely access starMapImageUrl.
  if (selectedData && typeof selectedData === 'object' && selectedData !== null && 'starMapImageUrl' in selectedData) {
    const imageUrl = selectedData['starMapImageUrl'];
    return typeof imageUrl === 'string' ? imageUrl : null;
  }
  
  return null;
}

/**
 * Converts a data URL to a File object.
 * 
 * @param {string} dataUrl - The data URL string
 * @param {string} filename - The filename for the File (default: 'star-map.png')
 * @returns {File} The File object
 * @throws {Error} If the data URL is invalid
 */
export function dataUrlToFile(dataUrl, filename = 'star-map.png') {
  if (!dataUrl || typeof dataUrl !== 'string') {
    throw new Error('Invalid data URL provided');
  }
  
  const arr = dataUrl.split(',');
  const mime = arr[0].match(/:(.*?);/)?.[1] || 'image/png';
  const bstr = atob(arr[1]);
  let n = bstr.length;
  const u8arr = new Uint8Array(n);
  while (n--) {
    u8arr[n] = bstr.charCodeAt(n);
  }
  return new File([u8arr], filename, { type: mime });
}

/**
 * Converts star map image URL to File object.
 * 
 * @param {string} starMapImageUrl - The star map image URL (data URL or regular URL)
 * @param {string} filename - The filename for the File (default: 'star-map.png')
 * @returns {File | null} The File object or null if conversion fails
 */
export function starMapImageUrlToFile(starMapImageUrl, filename = 'star-map.png') {
  if (!starMapImageUrl) {
    return null;
  }
  
  try {
    return dataUrlToFile(starMapImageUrl, filename);
  } catch (error) {
    console.error('Failed to convert star map image to File:', error);
    return null;
  }
}

